import { useState, useEffect } from "react";
import type { ActuatorData } from "./useGetVersionInfo.types";

export const useGetSystemInfo = () => {
    const [gitServiceInfo, setGitServiceInfo] = useState<ActuatorData | null>(
        null
    );
    const [backEndInfo, setBackEndInfo] = useState<ActuatorData | null>(null);

    const rawTimestamp: string = import.meta.env.VITE_BUILD_TIMESTAMP;

    const gitServiceActuator: string = encodeURI(
        import.meta.env.VITE_GITSERVICE_ACTUATOR_ENDPOINT
    );
    const backEndActuator: string = encodeURI(
        import.meta.env.VITE_BACKEND_ACTUATOR_ENDPOINT
    );

    const date = new Date(Number(rawTimestamp));

    const frontEndBuildTime: string = date.toISOString();

    const frontEndCommitHash: string = import.meta.env.VITE_COMMIT_HASH.replace(
        "\n",
        ""
    );
    const frontEndCommitTime: string = import.meta.env.VITE_COMMIT_TIME.replace(
        "\n",
        ""
    );

    const frontEndGitBranch = import.meta.env.VITE_GIT_BRANCH.replace("\n", "");

    const fetchActuatorData = async (url: string): Promise<ActuatorData> => {
        const response = await fetch(url, {
            method: "GET",
            mode: "cors",
            headers: { Accept: "application/json" },
        });
        if (!response.ok) {
            throw new Error(
                `Failed to fetch actuator data from ${url}: ${response.status} ${response.statusText}`
            );
        }
        return response.json();
    };

    useEffect(() => {
        const fetchGitServiceData = async () => {
            if (gitServiceInfo) return;

            try {
                const gitServiceData = await fetchActuatorData(
                    import.meta.env.VITE_GITSERVICE_ACTUATOR_URL
                );
                setGitServiceInfo(gitServiceData);
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : "Unknown error";
                console.error(
                    "Error fetching git service actuator data:",
                    errorMessage
                );
                setGitServiceInfo(null);
            }
        };
        fetchGitServiceData();
    }, [gitServiceActuator]);

    useEffect(() => {
        const fetchBackEndData = async () => {
            if (backEndInfo) return;

            try {
                const backEndData = await fetchActuatorData(
                    import.meta.env.VITE_BACKEND_ACTUATOR_URL
                );
                setBackEndInfo(backEndData);
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : "Unknown error";
                console.error(
                    "Error fetching backend actuator data:",
                    errorMessage
                );
                setBackEndInfo(null);
            }
        };
        fetchBackEndData();
    }, [backEndActuator]);

    return {
        frontEndBuildTime,
        frontEndCommitHash,
        frontEndCommitTime,
        frontEndGitBranch,
        gitServiceInfo,
        backEndInfo,
    };
};

export default useGetSystemInfo;
